<template>
  <textarea ref="textareaRef" />
</template>

<script lang="ts">
// https://alex-d.github.io/Trumbowyg/documentation/#events
const events = [
  'init', 'focus', 'blur', 'change', 'resize', 'paste', 'openfullscreen', 'closefullscreen', 'close', 'modalopen', 'modalclose'
];

const eventPrefix = 'tbw';
</script>

<script setup lang="ts">
// Need full jQuery build
import jQuery from 'jquery';
import { useI18n } from "vue-i18n";
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import 'trumbowyg';
// You have to import css yourself
import svgIcons from "trumbowyg/dist/ui/icons.svg";

const { locale } = useI18n();

const props = withDefaults(defineProps<{
  value: string | null;
  // http://alex-d.github.io/Trumbowyg/documentation.html#basic-options
  config?: Record<string, unknown>;
  // https://alex-d.github.io/Trumbowyg/documentation/#svg-icons
  svgPath?: string | boolean;
  disabled?: boolean;
}>(), {
  value: null,
  config: () => ({}),
  svgPath: svgIcons,
  disabled: false,
});

const emit = defineEmits([
  'input',
  'blur',
  ...events.map((name) => `${eventPrefix}-${name}`),
]);

const textareaRef = ref<HTMLTextAreaElement | null>(null);
let el: JQuery | null = null;

/**
 * Emit input event with current editor value
 * This will update v-model on parent component
 * This method gets called when value gets changed by editor itself
 */
function onChange(event: Event) {
  emit('input', (event.target as HTMLTextAreaElement).value);
}

/**
 * This event is different from tbw-blur event
 */
function onBlur(event: Event) {
  emit('blur', (event.target as HTMLTextAreaElement).value);
}

/**
 * Emit all available events
 */
function registerEvents() {
  events.forEach((name) => {
    el!.on(`${eventPrefix}${name}`, (...args: unknown[]) => {
      emit(`tbw-${name}`, ...args);
    });
  });
}

function toggleDisabled(value: boolean) {
  const method = value ? 'disable' : 'enable';
  el!.trumbowyg(method);
}

onMounted(() => {
  // Return early if instance is already loaded
  if (el) return;

  // Store DOM
  el = jQuery(textareaRef.value!);

  // Init editor with config
  el.trumbowyg(jQuery.extend(true, {}, { svgPath: props.svgPath }, { ...props.config, lang: locale.value }));
  // Set initial value
  el.trumbowyg('html', props.value);

  // Watch for further changes
  el.on(`${eventPrefix}change`, onChange);

  // Blur event for validation libraries
  el.on(`${eventPrefix}blur`, onBlur);

  // Initial value for disabled prop
  toggleDisabled(props.disabled);

  // Register events
  registerEvents();
});

/**
 * Listen to change from parent component and update DOM
 */
watch(() => props.value, (newValue) => {
  if (!el) return;
  // Prevent multiple input events
  if (newValue === el.trumbowyg('html')) return;
  // Set new value
  el.trumbowyg('html', newValue);
}, { deep: true });

watch(() => props.disabled, (newValue) => {
  toggleDisabled(newValue);
});

/**
 * Release memory
 */
onBeforeUnmount(() => {
  if (!el) return;

  el.trumbowyg('destroy');
  el = null;
});
</script>